import { format } from "date-fns";

/**
 * Builds URLSearchParams for AR Dashboard endpoints.
 * Omits undefined/null values. Repeats array params per id.
 */
export type DashboardQueryParams = {
	ltlBrokerIds?: number[];
	primaryBrokerIds?: number[];
	customerIds?: number[];
	startDate?: Date | string | null;
	endDate?: Date | string | null;
	carrierId?: number | null;
	selectedCustomerId?: number | null;
	search?: string | null;
};

const toIsoString = (value: Date | string): string =>
	typeof value === "string" ? value : value.toISOString();

/**
 * Formats a value as a date-only (yyyy-MM-dd) string for .NET `DateOnly`
 * parameters. Date instances are formatted using their local calendar date to
 * preserve the user-selected day (avoids the off-by-one that UTC conversion
 * would introduce for end-of-day boundaries). String inputs keep their leading
 * date portion.
 */
const toDateOnlyString = (value: Date | string): string =>
	typeof value === "string" ? value.slice(0, 10) : format(value, "yyyy-MM-dd");

const appendIfPresent = (
	searchParams: URLSearchParams,
	key: string,
	value: string | number | null | undefined,
) => {
	if (value !== undefined && value !== null) {
		searchParams.append(key, value.toString());
	}
};

export const buildDashboardSearchParams = (
	params: DashboardQueryParams,
	options?: { dateOnly?: boolean },
): URLSearchParams => {
	const searchParams = new URLSearchParams();

	const formatDate = options?.dateOnly ? toDateOnlyString : toIsoString;

	const arrayParams = {
		ltlBrokerIds: params.ltlBrokerIds,
		primaryBrokerIds: params.primaryBrokerIds,
		customerIds: params.customerIds,
	};

	for (const [key, values] of Object.entries(arrayParams)) {
		if (values && values.length > 0) {
			for (const id of values) {
				searchParams.append(key, id.toString());
			}
		}
	}

	appendIfPresent(
		searchParams,
		"startDate",
		params.startDate == null ? null : formatDate(params.startDate),
	);
	appendIfPresent(
		searchParams,
		"endDate",
		params.endDate == null ? null : formatDate(params.endDate),
	);
	appendIfPresent(searchParams, "carrierId", params.carrierId);
	appendIfPresent(
		searchParams,
		"selectedCustomerId",
		params.selectedCustomerId,
	);
	appendIfPresent(searchParams, "search", params.search);

	return searchParams;
};
